import { danaBatchCheck, danaBatchOrderPriceCheck, danaGetPriceLines, DanaPriceLineDto, danaSubmitCheck } from "@dana/Overrides/Services/CartService";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { css } from "styled-components";

type StepStatus = "pending" | "active" | "completed" | "failed";

export interface PriceCompareLine {
    id: string;
    productName: string;
    erpNumber: string;
    qtyOrdered: number;
    oldUnitPriceDisplay: string;
    newUnitPriceDisplay: string;
    oldUnitPrice: number;
    newUnitPrice: number;
    changed: boolean;
}

export interface PriceChangeModalStyles {
    modal?: ModalPresentationProps;
    progressWrapper?: InjectableCss;
    stepsWrapper?: InjectableCss;
    priceChangeBanner?: InjectableCss;
    priceChangeBannerTitle?: TypographyPresentationProps;
    noteText?: TypographyPresentationProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    productHeader?: DataTableHeaderProps;
    qtyHeader?: DataTableHeaderProps;
    oldPriceHeader?: DataTableHeaderProps;
    newPriceHeader?: DataTableHeaderProps;
    productCell?: DataTableCellProps;
    qtyCell?: DataTableCellProps;
    priceCell?: DataTableCellProps;
    subtotalWrapper?: InjectableCss;
    subtotalLabel?: TypographyPresentationProps;
    subtotalValue?: TypographyPresentationProps;
    errorText?: TypographyPresentationProps;
    buttonsWrapper?: InjectableCss;
    continueButton?: ButtonPresentationProps;
}

export const priceChangeModalStyles: PriceChangeModalStyles = {
    modal: { sizeVariant: "large" },
    progressWrapper: {
        css: css`
            margin-bottom: 24px;
        `,
    },
    stepsWrapper: {
        css: css`
            @keyframes pcm-spin { to { transform: rotate(360deg); } }

            display: flex;
            flex-direction: column;
            margin-bottom: 8px;

            .pcm-step {
                display: flex;
                gap: 13px;
                align-items: flex-start;
                padding: 10px 0;
                position: relative;
            }
            .pcm-step:not(:last-child)::after {
                content: '';
                position: absolute;
                left: 13px;
                top: 38px;
                bottom: 0;
                width: 2px;
                background: #e2e8f0;
                z-index: 0;
            }
            .pcm-step.completed:not(:last-child)::after {
                background: #a3d9b1;
            }
            .pcm-step.active:not(:last-child)::after {
                background: linear-gradient(to bottom, #93c5fd, #e2e8f0);
            }

            .pcm-dot {
                width: 28px;
                height: 28px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
                font-size: 12px;
                font-weight: 700;
                border: 2px solid #e2e8f0;
                background: #ffffff;
                color: #94a3b8;
                position: relative;
                z-index: 1;
                transition: background-color 0.25s, border-color 0.25s, color 0.25s;
            }
            .pcm-dot.active    { background: #eff6ff; border-color: #1d5fa6; color: #1d5fa6; }
            .pcm-dot.completed { background: #e8f5ec; border-color: #1a7a34; color: #1a7a34; }
            .pcm-dot.failed    { background: #fff1f2; border-color: #e11d48; color: #e11d48; }

            .pcm-spinner {
                width: 14px;
                height: 14px;
                border: 2px solid #93c5fd;
                border-top-color: #1d5fa6;
                border-radius: 50%;
                animation: pcm-spin 0.65s linear infinite;
                display: block;
            }

            .pcm-step-info { flex: 1; padding-top: 3px; }

            .pcm-step-name {
                font-size: 13px;
                font-weight: 600;
                color: #94a3b8;
                transition: color 0.2s;
            }
            .pcm-step-name.active    { color: #1d5fa6; }
            .pcm-step-name.completed { color: #1a7a34; }
            .pcm-step-name.failed    { color: #e11d48; }

            .pcm-step-desc {
                font-size: 11.5px;
                color: #94a3b8;
                margin-top: 2px;
            }
            .pcm-step-desc.active  { color: rgba(29,95,166,0.8); }
            .pcm-step-desc.failed  { color: rgba(225,29,72,0.9); }
        `,
    },
    priceChangeBanner: {
        css: css`
            background: #fffbeb;
            border: 1px solid #fcd34d;
            border-radius: 6px;
            padding: 14px 16px;
            margin-top: 16px;
        `,
    },
    priceChangeBannerTitle: {
        css: css`
            font-size: 13px;
            font-weight: 700;
            color: #92400e;
            margin-bottom: 8px;
            display: flex;
            align-items: center;
            gap: 6px;
        `,
    },
    noteText: {
        css: css`
            font-size: 12px;
            color: #78350f;
            margin-bottom: 12px;
            line-height: 1.55;
        `,
    },
    dataTable: {
        css: css`
            table-layout: fixed;
            width: 100%;
        `,
    },
    dataTableHead: {},
    dataTableBody: {},
    dataTableRow: {},
    productHeader: {
        css: css`
            width: 50%;
            text-align: left;
            justify-content: flex-start;
        `,
    },
    qtyHeader: {
        css: css`
            width: 10%;
            text-align: center;
            justify-content: center;
        `,
    },
    oldPriceHeader: {
        css: css`
            width: 20%;
            text-align: right;
            justify-content: flex-end;
        `,
    },
    newPriceHeader: {
        css: css`
            width: 20%;
            text-align: right;
            justify-content: flex-end;
        `,
    },
    productCell: {
        css: css`
            width: 50%;
            text-align: left;
            vertical-align: top;
        `,
    },
    qtyCell: {
        css: css`
            width: 10%;
            text-align: center;
            vertical-align: top;
        `,
    },
    priceCell: {
        css: css`
            width: 20%;
            text-align: right;
            vertical-align: top;
        `,
    },
    subtotalWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            align-items: center;
            gap: 32px;
            padding: 12px 0;
            border-top: 1px solid ${({ theme }: { theme: BaseTheme }) => theme.colors.common.border};
            margin-top: 8px;
        `,
    },
    subtotalLabel: { weight: "bold" },
    subtotalValue: {},
    errorText: {
        color: "danger",
        css: css`
            margin-top: 12px;
            margin-bottom: 4px;
        `,
    },
    buttonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            gap: 12px;
            margin-top: 16px;
        `,
    },
    continueButton: { variant: "primary" },
};

export interface PriceChangeModalProps {
    isOpen: boolean;
    orderNumber: string | null;
    orderCartId: string | null;
    isBatchSubmit: boolean;
    originalCartLines: CartLineModel[] | null | undefined;
    originalOrderSubTotal: number | undefined;
    onFinish: (cartId: string) => void;
    onError: () => void;
    extendedStyles?: PriceChangeModalStyles;
    isEmergencyOrder: boolean
}

const STEP2_POLL_MS = 5000;
const STEP3_POLL_MS = 10000;
const OVERALL_TIMEOUT_MS = 300000;

const StepDot = ({ status, number }: { status: StepStatus; number: number }) => (
    <div className={`pcm-dot ${status}`}>
        {status === "active"
            ? <span className="pcm-spinner" />
            : status === "completed"
                ? "✓"
                : status === "failed"
                    ? "✕"
                    : number}
    </div>
);

const OrderProcessSteps = ({
    isOpen,
    orderNumber,
    orderCartId,
    isBatchSubmit,
    originalCartLines,
    isEmergencyOrder,
    onFinish,
    extendedStyles,
    
}: PriceChangeModalProps) => {
    const styles = extendedStyles ? mergeToNew(priceChangeModalStyles, extendedStyles) : priceChangeModalStyles;

    // Step 1: Processing Order (placeOrder API in flight)
    // Step 2: Price Verification (danaBatchCheck polling)
    // Step 3: Order Submission (danaSubmitCheck polling)
    const [step1Status, setStep1Status] = useState<StepStatus>("pending");
    const [step2Status, setStep2Status] = useState<StepStatus>("pending");
    const [step3Status, setStep3Status] = useState<StepStatus>("pending");
    const [progressPct, setProgressPct] = useState(0);
    const [priceCompareLines, setPriceCompareLines] = useState<PriceCompareLine[]>([]);
    const [newSubTotalDisplay, setNewSubTotalDisplay] = useState<string>("");
    const [awaitingContinue, setAwaitingContinue] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const pollRef2 = useRef<NodeJS.Timeout | null>(null);
    const pollRef3 = useRef<NodeJS.Timeout | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const progressRef = useRef(0);
    // Tracks whether step 1→2 transition already happened (prevents double-start on re-render)
    const step2StartedRef = useRef(false);

    // Mutable refs to avoid stale closures inside interval callbacks
    const orderNumberRef = useRef(orderNumber);
    const orderCartIdRef = useRef(orderCartId);
    const isBatchSubmitRef = useRef(isBatchSubmit);
    const originalCartLinesRef = useRef(originalCartLines);
    const onFinishRef = useRef(onFinish);

    useEffect(() => { orderNumberRef.current = orderNumber; }, [orderNumber]);
    useEffect(() => { orderCartIdRef.current = orderCartId; }, [orderCartId]);
    useEffect(() => { isBatchSubmitRef.current = isBatchSubmit; }, [isBatchSubmit]);
    useEffect(() => { originalCartLinesRef.current = originalCartLines; }, [originalCartLines]);
    useEffect(() => { onFinishRef.current = onFinish; }, [onFinish]);

    const clearAllTimers = () => {
        if (pollRef2.current) { clearInterval(pollRef2.current); pollRef2.current = null; }
        if (pollRef3.current) { clearInterval(pollRef3.current); pollRef3.current = null; }
        if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
        if (progressIntervalRef.current) { clearInterval(progressIntervalRef.current); progressIntervalRef.current = null; }
    };

    const animateProgressTo = (target: number, intervalMs = 200) => {
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = setInterval(() => {
            if (progressRef.current >= target) {
                clearInterval(progressIntervalRef.current!);
                progressIntervalRef.current = null;
                return;
            }
            progressRef.current = Math.min(progressRef.current + 1, target);
            setProgressPct(progressRef.current);
        }, intervalMs);
    };

    const startStep3Polling = () => {
        setStep3Status("active");
        animateProgressTo(95, 250);

        if (!isBatchSubmitRef.current) {
            // Non-batch: complete immediately
            progressRef.current = 100;
            setProgressPct(100);
            setStep3Status("completed");
            setTimeout(() => {
                const cid = orderCartIdRef.current;
                if (cid) onFinishRef.current(cid);
            }, 400);
            return;
        }

        pollRef3.current = setInterval(() => {
            const on = orderNumberRef.current;
            if (!on) return;
            danaSubmitCheck(on)
                .then(result => {
                    if (result.success) {
                        clearInterval(pollRef3.current!);
                        pollRef3.current = null;
                        setStep3Status("completed");
                        progressRef.current = 100;
                        setProgressPct(100);
                        setTimeout(() => {
                            const cid = orderCartIdRef.current;
                            if (cid) onFinishRef.current(cid);
                        }, 500);
                    }
                })
                .catch(() => { /* continue polling on transient errors */ });
        }, STEP3_POLL_MS);
    };

    // Called once danaBatchCheck signals success — fetch actual priced lines
    const handleBatchCheckSuccess = (orderCartId :string) => {
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        progressRef.current = 65;
        setProgressPct(65);

        danaGetPriceLines(isEmergencyOrder)
            .then(priceResult => {
                setStep2Status("completed");

                if (!priceResult.success) {
                    startStep3Polling();
                    return;
                }

                const origLines = originalCartLinesRef.current ?? [];
                if (origLines.length === 0) {
                    startStep3Polling();
                    return;
                }

                // Build comparison: match by erpNumber (backend line id differs from cart line id)
                const compareLines: PriceCompareLine[] = origLines.map(origLine => {
                    const erp = origLine.erpNumber ?? "";
                    const newLine = priceResult.lines.find(
                        (l: DanaPriceLineDto) => l.erpNumber === erp || l.id === origLine.id
                    );
                    const oldPrice = origLine.pricing?.unitNetPrice ?? 0;
                    const newPrice = newLine?.unitNetPrice ?? oldPrice;
                    return {
                        id: origLine.id,
                        productName: origLine.productName ?? newLine?.productName ?? "",
                        erpNumber: erp,
                        qtyOrdered: origLine.qtyOrdered ?? 0,
                        oldUnitPriceDisplay: origLine.pricing?.unitNetPriceDisplay ?? `$${oldPrice.toFixed(2)}`,
                        newUnitPriceDisplay: newLine?.unitNetPriceDisplay ?? `$${newPrice.toFixed(2)}`,
                        oldUnitPrice: oldPrice,
                        newUnitPrice: newPrice,
                        changed: Math.abs(newPrice - oldPrice) > 0.001,
                    };
                });

                const hasPriceChange = compareLines.some(l => l.changed);

                if (hasPriceChange) {
                    setPriceCompareLines(compareLines);
                    setNewSubTotalDisplay(priceResult.orderSubTotalDisplay);
                    setAwaitingContinue(true);
                } else {
                    startStep3Polling();
                }
            })
            .catch(() => {
                setStep2Status("completed");
                startStep3Polling();
            });
    };

    const startStep2Polling = () => {
        setStep2Status("active");
        animateProgressTo(60, 200);
        if (orderCartId) {
            pollRef2.current = setInterval(() => {
                danaBatchOrderPriceCheck(orderCartId)
                    .then(result => {
                        if (result.success) {
                            clearInterval(pollRef2.current!);
                            pollRef2.current = null;
                            handleBatchCheckSuccess(orderCartId);
                        }
                    })
                    .catch(() => { /* continue polling on transient errors */ });
            }, STEP2_POLL_MS);
        }
    };

    useEffect(() => {
        if (!isOpen) {
            clearAllTimers();
            step2StartedRef.current = false;
            setStep1Status("pending");
            setStep2Status("pending");
            setStep3Status("pending");
            progressRef.current = 0;
            setProgressPct(0);
            setPriceCompareLines([]);
            setNewSubTotalDisplay("");
            setAwaitingContinue(false);
            setErrorMsg(null);
            return;
        }

        // Activate step 1 as soon as modal opens (placeOrder is in flight)
        setStep1Status("active");
        animateProgressTo(25, 300);

        timeoutRef.current = setTimeout(() => {
            clearAllTimers();
            setErrorMsg(translate("Order processing timed out. Please contact support."));
            setStep1Status(s => s !== "completed" ? "failed" : s);
            setStep2Status(s => s !== "completed" ? "failed" : s);
            setStep3Status(s => s !== "completed" ? "failed" : s);
        }, OVERALL_TIMEOUT_MS);

        return () => clearAllTimers();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !orderNumber || !orderCartId) return;
        if (step2StartedRef.current) return;
        step2StartedRef.current = true;

        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        progressRef.current = 33;
        setProgressPct(33);
        setStep1Status("completed");
        startStep2Polling();
    }, [isOpen, orderNumber, orderCartId]);

    const handleContinue = () => {
        setAwaitingContinue(false);
        startStep3Polling();
    };

    const changedLines = useMemo(() => priceCompareLines.filter(l => l.changed), [priceCompareLines]);

    const progressColor = step3Status === "completed" ? "#1a7a34" : "#1d5fa6";
    const progressLabel =
        step3Status === "completed" ? translate("Order Complete") :
            step3Status === "active" ? translate("Submitting order...") :
                step2Status === "active" ? translate("Verifying pricing...") :
                    step1Status === "active" ? translate("Processing order...") :
                        translate("Processing order...");

    const step1Desc = () => {
        if (step1Status === "pending") return translate("Waiting to start...");
        if (step1Status === "active") return translate("Submitting order to the system...");
        if (step1Status === "completed") return translate("Order accepted.");
        return translate("Order processing failed.");
    };

    const step2Desc = () => {
        if (step2Status === "pending") return translate("Waiting for order to be accepted...");
        if (step2Status === "active") return translate("Verifying pricing with ERP...");
        if (step2Status === "completed" && awaitingContinue) return translate("Price changes detected — review below.");
        if (step2Status === "completed") return translate("Pricing verified.");
        return translate("Price verification failed.");
    };

    const step3Desc = () => {
        if (step3Status === "pending") return translate("Waiting for price verification...");
        if (step3Status === "active") return translate("Submitting order to ERP...");
        if (step3Status === "completed") return translate("Order submitted successfully.");
        return translate("Order submission failed.");
    };

    return (
        <Modal
            {...styles.modal}
            headline={translate("Processing your order...")}
            isOpen={isOpen}
            handleClose={() => {  }}
            data-test-selector="onePageCheckout_orderProcessModal"
        >
           
            <StyledWrapper {...styles.progressWrapper}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#94a3b8", marginBottom: 5 }}>
                    <span>{progressLabel}</span>
                    <span>{progressPct}%</span>
                </div>
                <div
                    style={{ height: 6, background: "#f1f5f9", borderRadius: 999, overflow: "hidden", border: "1px solid #e2e8f0" }}
                    role="progressbar"
                    aria-valuenow={progressPct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={translate("Order processing progress")}
                >
                    <div style={{
                        width: `${progressPct}%`,
                        height: "100%",
                        background: progressColor,
                        borderRadius: 999,
                        transition: "width 0.3s ease, background-color 0.3s",
                    }} />
                </div>
            </StyledWrapper>
            <StyledWrapper {...styles.stepsWrapper}>
                <div className={`pcm-step ${step1Status}`}>
                    <StepDot status={step1Status} number={1} />
                    <div className="pcm-step-info">
                        <div className={`pcm-step-name ${step1Status}`}>{translate("Processing Order")}</div>
                        <div className={`pcm-step-desc ${step1Status}`}>{step1Desc()}</div>
                    </div>
                </div>

                <div className={`pcm-step ${step2Status}`}>
                    <StepDot status={step2Status} number={2} />
                    <div className="pcm-step-info">
                        <div className={`pcm-step-name ${step2Status}`}>{translate("Price Verification")}</div>
                        <div className={`pcm-step-desc ${step2Status}`}>{step2Desc()}</div>
                    </div>
                </div>

                <div className={`pcm-step ${step3Status}`}>
                    <StepDot status={step3Status} number={3} />
                    <div className="pcm-step-info">
                        <div className={`pcm-step-name ${step3Status}`}>{translate("Order Submission")}</div>
                        <div className={`pcm-step-desc ${step3Status}`}>{step3Desc()}</div>
                    </div>
                </div>
            </StyledWrapper>

           
            {awaitingContinue && changedLines.length > 0 && (
                <StyledWrapper {...styles.priceChangeBanner}>
                    <Typography {...styles.priceChangeBannerTitle}>
                        <span style={{ lineHeight: 1 }}>&#9888;</span>
                        <span>{translate("Price Change Warning")}</span>
                    </Typography>
                    <Typography {...styles.noteText}>
                        {translate("One or more product prices have changed since being added to your cart. Review the updated prices below, then click Continue to proceed with order submission.")}
                    </Typography>

                    <DataTable {...styles.dataTable}>
                        <DataTableHead {...styles.dataTableHead}>
                            <DataTableHeader {...styles.productHeader}>{translate("Product")}</DataTableHeader>
                            <DataTableHeader {...styles.qtyHeader}>{translate("Qty")}</DataTableHeader>
                            <DataTableHeader {...styles.oldPriceHeader}>{translate("Original Price")}</DataTableHeader>
                            <DataTableHeader {...styles.newPriceHeader}>{translate("Updated Price")}</DataTableHeader>
                        </DataTableHead>
                        <DataTableBody {...styles.dataTableBody}>
                            {changedLines.map(line => (
                                <DataTableRow key={line.id} {...styles.dataTableRow}>
                                    <DataTableCell {...styles.productCell}>
                                        <div>{line.productName}</div>
                                        <div style={{ fontSize: 11, color: "#64748b" }}>{line.erpNumber}</div>
                                    </DataTableCell>
                                    <DataTableCell {...styles.qtyCell}>{line.qtyOrdered}</DataTableCell>
                                    <DataTableCell {...styles.priceCell}>
                                        <s>{line.oldUnitPriceDisplay}</s>
                                    </DataTableCell>
                                    <DataTableCell {...styles.priceCell}>
                                        <span style={{ fontWeight: 700, color: "#d97706" }}>{line.newUnitPriceDisplay}</span>
                                    </DataTableCell>
                                </DataTableRow>
                            ))}
                        </DataTableBody>
                    </DataTable>

                    {newSubTotalDisplay && (
                        <StyledWrapper {...styles.subtotalWrapper}>
                            <Typography {...styles.subtotalLabel}>{translate("Updated Order Subtotal:")}</Typography>
                            <Typography {...styles.subtotalValue}>{newSubTotalDisplay}</Typography>
                        </StyledWrapper>
                    )}

                    <StyledWrapper {...styles.buttonsWrapper}>
                        <Button
                            {...styles.continueButton}
                            onClick={handleContinue}
                            data-test-selector="orderProcess_continueAfterPriceChange"
                        >
                            {translate("Continue")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            )}

            {errorMsg && (
                <Typography {...styles.errorText}>{errorMsg}</Typography>
            )}
        </Modal>
    );
};

export default OrderProcessSteps;
